import { useEffect, useState } from "react";
import { supAPI } from "./supApi";
import { toast } from "sonner";
import { Plus, Check, X as XIcon, Ban, Trash2 } from "lucide-react";
import { EmptyState } from "./SupplyItems";
import { Modal, Fld, Footer, cls } from "./SupplyStock";
import { NumberInput } from "@/components/ui/number-input";

const STATUS_STYLES = {
  submitted: "bg-amber-50 text-amber-700",
  approved:  "bg-emerald-50 text-emerald-700",
  rejected:  "bg-rose-50 text-rose-700",
  cancelled: "bg-slate-100 text-slate-500",
  converted: "bg-sky-50 text-sky-700",
};

export default function SupplyPRs() {
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);

  const load = () => supAPI.prs({ status: status || undefined }).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => { supAPI.items().then(setItems); }, []);

  return (
    <div className="space-y-4" data-testid="sup-prs">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="sup-pr-filter" aria-label="Filter by status"
                className={cls + " max-w-[200px]"}>
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="converted">Converted to PO</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={() => setCreating(true)} disabled={!items.length}
                data-testid="sup-pr-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4"/> New Request
        </button>
      </div>

      {!rows.length ? <EmptyState msg="No purchase requests yet."/> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11.5px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Requested by</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(r => (
                <tr key={r.id} data-testid={`sup-pr-row-${r.id}`} onClick={() => setSelectedPR(r)}
                    className="hover:bg-slate-50/50 cursor-pointer">
                  <td className="px-4 py-3 mono text-slate-500">{r.code}</td>
                  <td className="px-4 py-3 text-slate-600">{r.requested_by_email}</td>
                  <td className="px-4 py-3 text-slate-600">{(r.items || []).length} line(s)</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLES[r.status]}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-right"><span className="text-slate-400 text-[11.5px]">View →</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && <PRCreateDialog items={items} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }}/>}
      {selectedPR && <PRDetailDialog pr={selectedPR} items={items}
                                       onClose={() => setSelectedPR(null)}
                                       onDone={() => { setSelectedPR(null); load(); }}/>}
    </div>
  );
}

function PRCreateDialog({ items, onClose, onDone }) {
  const [lines, setLines] = useState([{ item_id: items[0]?.id || "", qty: 1, notes: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const add = () => setLines(prev => [...prev, { item_id: items[0]?.id || "", qty: 1, notes: "" }]);
  const rm = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));
  const lineErr = (l) => !l.item_id ? "Pick an item" : (!(Number(l.qty) > 0) ? "Qty must be greater than 0" : "");

  const submit = async () => {
    setSubmitted(true);
    const valid = lines.filter(l => l.item_id && Number(l.qty) > 0);
    if (!valid.length) { toast.error("Add at least one valid line (item + quantity)"); return; }
    setSaving(true);
    try {
      await supAPI.createPR({ items: valid.map(l => ({ ...l, qty: Number(l.qty) })), notes: notes || undefined });
      toast.success("Purchase request submitted"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Purchase Request" onClose={onClose}>
      <div className="space-y-3">
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">
            <span>Item <span className="text-rose-500">*</span></span><span>Qty <span className="text-rose-500">*</span></span><span>Line notes</span><span></span>
          </div>
          {lines.map((l, i) => {
            const err = submitted ? lineErr(l) : "";
            return (
            <div key={i}>
              <div className="grid grid-cols-[1fr_100px_1fr_auto] gap-2 items-center">
                <select value={l.item_id} onChange={e => update(i, { item_id: e.target.value })}
                        data-testid={`pr-line-item-${i}`} aria-label="Item" className={`${cls} ${err && !l.item_id ? "border-rose-400" : ""}`}>
                  {items.map(it => <option key={it.id} value={it.id}>{it.sku} — {it.name} ({it.uom})</option>)}
                </select>
                <NumberInput min="0" value={l.qty} onChange={e => update(i, { qty: e.target.value })}
                       data-testid={`pr-line-qty-${i}`} aria-label="Quantity" className={`${cls} ${err && !(Number(l.qty) > 0) ? "border-rose-400" : ""}`}/>
                <input value={l.notes} onChange={e => update(i, { notes: e.target.value })}
                       placeholder="notes (optional)" aria-label="Line notes" className={cls}/>
                <button onClick={() => rm(i)} disabled={lines.length === 1}
                        className="p-2 rounded-lg hover:bg-rose-50 text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
              {err && <p className="text-[11px] text-rose-500 mt-1" data-testid={`pr-line-error-${i}`}>{err}</p>}
            </div>
          );})}
          <button onClick={add} data-testid="pr-add-line"
                  className="text-[12px] text-hocp-navy font-medium hover:underline">+ Add another line</button>
        </div>
        <Fld label="Notes (optional)" full>
          <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                    className={cls + " py-2 h-auto"}/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="pr-submit"/>
    </Modal>
  );
}

function PRDetailDialog({ pr, items, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const itemMap = Object.fromEntries(items.map(i => [i.id, i]));

  const act = async (fn, label) => {
    setBusy(true);
    try { await fn(); toast.success(label); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Purchase Request ${pr.code}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 text-[13px]">
          <Info label="Status"><span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_STYLES[pr.status]}`}>{pr.status}</span></Info>
          <Info label="Requested by">{pr.requested_by_email}</Info>
          <Info label="Created">{(pr.created_at || "").replace("T", " ").slice(0, 16)}</Info>
          {pr.approver_notes && <Info label="Approver notes">{pr.approver_notes}</Info>}
          {pr.rejected_reason && <Info label="Rejected reason">{pr.rejected_reason}</Info>}
        </div>
        <div>
          <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-2">Items</div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase">
                <tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Qty</th><th className="text-left px-3 py-2">Notes</th></tr>
              </thead>
              <tbody>
                {(pr.items || []).map((li, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-2">{itemMap[li.item_id]?.name || li.item_id}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{li.qty} {itemMap[li.item_id]?.uom || ""}</td>
                    <td className="px-3 py-2 text-slate-500">{li.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {pr.notes && (
          <div>
            <div className="text-[11.5px] uppercase tracking-wider text-slate-500 font-medium mb-1">Notes</div>
            <div className="text-[13px] text-slate-700">{pr.notes}</div>
          </div>
        )}
      </div>

      {pr.status === "submitted" && (
        <div className="pt-4 mt-4 border-t border-slate-100 flex justify-end gap-2 -mx-5 -mb-5 px-5 py-3 bg-slate-50">
          <button onClick={() => act(() => supAPI.cancelPR(pr.id), "Cancelled")} disabled={busy}
                  data-testid="pr-cancel"
                  className="h-9 px-3.5 rounded-lg text-slate-600 text-[13px] hover:bg-slate-100 inline-flex items-center gap-1.5">
            <Ban className="w-4 h-4"/> Cancel
          </button>
          <button onClick={() => {
                    const reason = prompt("Reject reason?");
                    if (reason) act(() => supAPI.rejectPR(pr.id, { reason }), "Rejected");
                  }} disabled={busy} data-testid="pr-reject"
                  className="h-9 px-3.5 rounded-lg text-rose-700 text-[13px] font-medium hover:bg-rose-50 inline-flex items-center gap-1.5">
            <XIcon className="w-4 h-4"/> Reject
          </button>
          <button onClick={() => act(() => supAPI.approvePR(pr.id, { notes: "Approved" }), "Approved")}
                  disabled={busy} data-testid="pr-approve"
                  className="h-9 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2">
            <Check className="w-4 h-4"/> Approve
          </button>
        </div>
      )}
    </Modal>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
      <div className="text-[13px] text-slate-800">{children}</div>
    </div>
  );
}
